import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  RadioGroup,
  RadioGroupItem,
} from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { submitQuote } from "@/lib/api";

const PLAN_OPTIONS = [
  "ACA Health",
  "Medicare Advantage",
  "Medicare Supplement",
  "Dental",
  "Hospital Indemnity",
  "Cancer Plans",
  "Long Term Care",
  "Final Expense",
  "Not Sure",
];

const STEPS = [
  { id: 1, label: "ZIP" },
  { id: 2, label: "Plan" },
  { id: 3, label: "About You" },
  { id: 4, label: "Contact" },
];

export const QuoteWizard = ({ initialPlan }) => {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [data, setData] = useState({
    zip_code: "",
    plan_type: initialPlan || "Not Sure",
    age: "",
    household_size: 1,
    tobacco_user: false,
    name: "",
    email: "",
    phone: "",
    preferred_contact: "phone",
    notes: "",
  });

  const update = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const validateStep = () => {
    if (step === 1) {
      if (!/^\d{5}$/.test(data.zip_code)) {
        toast.error("Please enter a valid 5-digit ZIP code.");
        return false;
      }
    }
    if (step === 2 && !data.plan_type) {
      toast.error("Please select a plan type.");
      return false;
    }
    if (step === 3) {
      const ageNum = parseInt(data.age, 10);
      if (isNaN(ageNum) || ageNum < 0 || ageNum > 120) {
        toast.error("Please enter a valid age (0–120).");
        return false;
      }
    }
    if (step === 4) {
      if (!data.name || !data.email || !data.phone) {
        toast.error("Name, email, and phone are required.");
        return false;
      }
    }
    return true;
  };

  const next = () => {
    if (!validateStep()) return;
    setStep((s) => Math.min(s + 1, STEPS.length));
  };
  const back = () => setStep((s) => Math.max(s - 1, 1));

  const onSubmit = async () => {
    if (!validateStep()) return;
    setSubmitting(true);
    try {
      const payload = {
        ...data,
        age: parseInt(data.age, 10),
        household_size: parseInt(data.household_size, 10) || 1,
      };
      await submitQuote(payload);
      setDone(true);
      toast.success("Quote request received! Christian will be in touch shortly.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not submit your request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="quote-wizard-success"
        className="rounded-2xl border border-emerald-200 bg-emerald-50 p-10 text-center"
      >
        <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-600" />
        <h3 className="font-display text-3xl mt-5 text-emerald-900">
          Your quote request is in.
        </h3>
        <p className="mt-3 max-w-xl mx-auto text-sm text-emerald-800/90 leading-relaxed">
          Christian Regalado personally reviews every quote and will contact you within
          one business day with options tailored to your situation. Need it sooner?
          Call <strong>850-687-7606</strong>.
        </p>
      </div>
    );
  }

  return (
    <div
      data-testid="quote-wizard"
      className="rounded-2xl border border-slate-200 bg-white p-7 sm:p-10"
    >
      {/* Stepper */}
      <ol className="flex items-center justify-between gap-2 mb-10">
        {STEPS.map((s, idx) => (
          <li key={s.id} className="flex-1 flex items-center gap-2">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                step >= s.id
                  ? "bg-[var(--brand-primary)] text-white"
                  : "bg-slate-100 text-slate-500"
              }`}
              data-testid={`quote-step-indicator-${s.id}`}
            >
              {step > s.id ? <CheckCircle2 className="w-4 h-4" /> : s.id}
            </div>
            <span className="hidden sm:inline text-xs uppercase tracking-[0.18em] text-slate-500">
              {s.label}
            </span>
            {idx < STEPS.length - 1 && (
              <span className="hidden md:block flex-1 h-px bg-slate-200" />
            )}
          </li>
        ))}
      </ol>

      {step === 1 && (
        <div data-testid="quote-step-1">
          <p className="eyebrow">Step 1</p>
          <h3 className="font-display text-3xl mt-2 text-[var(--ink)]">
            What’s your ZIP code?
          </h3>
          <p className="mt-3 text-sm text-slate-600">
            Plans and pricing vary by county. We use your ZIP to pull the right options.
          </p>
          <div className="mt-7 max-w-xs">
            <Label htmlFor="zip" className="text-xs uppercase tracking-[0.18em] text-slate-500">
              ZIP code
            </Label>
            <Input
              id="zip"
              data-testid="quote-input-zip"
              inputMode="numeric"
              maxLength={5}
              value={data.zip_code}
              onChange={(e) => update("zip_code", e.target.value.replace(/\D/g, ""))}
              placeholder=""
              className="mt-2 h-12 text-lg tracking-widest"
            />
          </div>
        </div>
      )}

      {step === 2 && (
        <div data-testid="quote-step-2">
          <p className="eyebrow">Step 2</p>
          <h3 className="font-display text-3xl mt-2 text-[var(--ink)]">
            Which type of plan?
          </h3>
          <p className="mt-3 text-sm text-slate-600">
            Not sure yet? That’s fine — pick ”Not Sure” and Christian will help you decide.
          </p>
          <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {PLAN_OPTIONS.map((p) => {
              const active = data.plan_type === p;
              return (
                <button
                  key={p}
                  type="button"
                  data-testid={`quote-plan-option-${p.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => update("plan_type", p)}
                  className={`text-left px-5 py-4 rounded-xl border transition-colors duration-200 ${
                    active
                      ? "border-[var(--brand-primary)] bg-[var(--bg-secondary)]"
                      : "border-slate-200 hover:border-slate-300 bg-white"
                  }`}
                >
                  <span className="font-display text-lg text-[var(--ink)]">{p}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {step === 3 && (
        <div data-testid="quote-step-3">
          <p className="eyebrow">Step 3</p>
          <h3 className="font-display text-3xl mt-2 text-[var(--ink)]">
            A bit about you
          </h3>
          <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <Label htmlFor="age" className="text-xs uppercase tracking-[0.18em] text-slate-500">
                Your age
              </Label>
              <Input
                id="age"
                data-testid="quote-input-age"
                inputMode="numeric"
                value={data.age}
                onChange={(e) => update("age", e.target.value.replace(/\D/g, ""))}
                placeholder="e.g. 62"
                className="mt-2 h-11"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.18em] text-slate-500">
                Household size
              </Label>
              <Select
                value={String(data.household_size)}
                onValueChange={(v) => update("household_size", parseInt(v, 10))}
              >
                <SelectTrigger data-testid="quote-select-household" className="mt-2 h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                    <SelectItem key={n} value={String(n)} data-testid={`quote-household-${n}`}>
                      {n === 1 ? "Just me" : `${n} people`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2 flex items-center gap-3 mt-1">
              <Checkbox
                id="tobacco"
                data-testid="quote-checkbox-tobacco"
                checked={data.tobacco_user}
                onCheckedChange={(v) => update("tobacco_user", !!v)}
              />
              <Label htmlFor="tobacco" className="text-sm text-slate-700 cursor-pointer">
                I use tobacco products
              </Label>
            </div>
          </div>
        </div>
      )}

      {step === 4 && (
        <div data-testid="quote-step-4">
          <p className="eyebrow">Step 4</p>
          <h3 className="font-display text-3xl mt-2 text-[var(--ink)]">
            Where should we send your options?
          </h3>
          <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <Label htmlFor="qname" className="text-xs uppercase tracking-[0.18em] text-slate-500">
                Full name
              </Label>
              <Input
                id="qname"
                data-testid="quote-input-name"
                value={data.name}
                onChange={(e) => update("name", e.target.value)}
                placeholder="Jane Beachcomber"
                className="mt-2 h-11"
              />
            </div>
            <div>
              <Label htmlFor="qphone" className="text-xs uppercase tracking-[0.18em] text-slate-500">
                Phone
              </Label>
              <Input
                id="qphone"
                data-testid="quote-input-phone"
                value={data.phone}
                onChange={(e) => update("phone", e.target.value)}
                placeholder="(850) 555-0123"
                className="mt-2 h-11"
              />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="qemail" className="text-xs uppercase tracking-[0.18em] text-slate-500">
                Email
              </Label>
              <Input
                id="qemail"
                data-testid="quote-input-email"
                value={data.email}
                onChange={(e) => update("email", e.target.value)}
                type="email"
                placeholder="you@example.com"
                className="mt-2 h-11"
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-[0.18em] text-slate-500">
                Preferred contact method
              </Label>
              <RadioGroup
                value={data.preferred_contact}
                onValueChange={(v) => update("preferred_contact", v)}
                className="mt-3 flex flex-wrap gap-3"
              >
                {[
                  { v: "phone", l: "Phone call" },
                  { v: "text", l: "Text message" },
                  { v: "email", l: "Email" },
                ].map((opt) => (
                  <label
                    key={opt.v}
                    htmlFor={`pc-${opt.v}`}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-full border cursor-pointer transition-colors ${
                      data.preferred_contact === opt.v
                        ? "border-[var(--brand-primary)] bg-[var(--bg-secondary)] text-[var(--brand-primary)]"
                        : "border-slate-200 text-slate-700 hover:border-slate-300"
                    }`}
                  >
                    <RadioGroupItem
                      value={opt.v}
                      id={`pc-${opt.v}`}
                      data-testid={`quote-contact-${opt.v}`}
                      className="sr-only"
                    />
                    {opt.l}
                  </label>
                ))}
              </RadioGroup>
            </div>
          </div>
        </div>
      )}

      <div className="mt-10 flex items-center justify-between gap-3">
        <Button
          type="button"
          variant="outline"
          data-testid="quote-back-button"
          disabled={step === 1 || submitting}
          onClick={back}
          className="rounded-full h-11"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>
        {step < STEPS.length ? (
          <Button
            type="button"
            data-testid="quote-next-button"
            onClick={next}
            className="rounded-full h-11 px-7 bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white"
          >
            Continue <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        ) : (
          <Button
            type="button"
            data-testid="quote-submit-button"
            onClick={onSubmit}
            disabled={submitting}
            className="rounded-full h-11 px-7 bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…
              </>
            ) : (
              <>
                Get my quote <ArrowRight className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );
};
